// App
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Components
import SharedMarketingBlurb from './MarketingBlurb';
import SrpContentWrapper from 'app/shared/modules/MobileSRP/__BaseComponents__/SrpContentWrapper';
// Local link group components
import MobileLinkGroup from './MobileLinkGroup';
import DesktopLinkGroup from './DesktopLinkGroup';

// Misc / utils
import searchSlugUtils from 'app/shared/utils/searchSlugUtils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { SeoLink, SeoLinkGroup } from 'app/shared/flux/reducers/seo';
import * as S from './styles';

const logger = getGlobalLogger('modules/mobilesrp/seofooter');

type SeoFooterProps = {
  hideDivider?: boolean;
  shortDivider?: boolean;
  className?: string;
  showMarketingBlurb?: boolean;
  disableInternalMarketingBlurb?: boolean;
};

const NEAR_BY_RENTALS_GROUP: SeoLinkGroup = {
  header: 'Near me',
  links: [
    { name: 'Rentals Near Me', uriV2: '/rentals-near-me' },
    { name: 'Homes for Rent Near Me', uriV2: '/homes-for-rent-near-me' },
    { name: 'Places for Rent Near Me', uriV2: '/places-for-rent-near-me' },
    { name: 'Cheap Apartments Near Me', uriV2: '/cheap-apartments-near-me' },
    { name: '1 Bedroom Apartments Near Me', uriV2: '/1-bedroom-apartments-near-me' },
  ],
};

const SeoFooter = ({
  hideDivider = false,
  shortDivider = false,
  className = '',
  showMarketingBlurb,
  disableInternalMarketingBlurb = false,
}: SeoFooterProps = {}) => {
  // Redux
  const seoLinks = useSelector((state: RootReduxState) => state.seo.seoFooterLinks);
  const pathname = useSelector((state: RootReduxState) => state.location.current.pathname);
  const isMobile = useSelector((state: RootReduxState) => state.app.device.isMobile);
  const slug = searchSlugUtils.getSearchSlugFromUrl(pathname);

  // Marketing blurb logic: use prop if provided, otherwise use original mobile logic
  // But if disableInternalMarketingBlurb is true, never show it internally
  const shouldShowMarketingBlurb = disableInternalMarketingBlurb
    ? false
    : showMarketingBlurb !== undefined
      ? showMarketingBlurb
      : slug === 'apartments-for-rent' || slug === 'houses-for-rent';

  // Filter out any links with /undefined/ in the URL to prevent displaying broken links
  const filteredSeoLinks = useMemo(() => {
    if (!seoLinks.groups) {
      return seoLinks;
    }

    const filteredGroups = seoLinks.groups
      .map((group: SeoLinkGroup) => {
        const validLinks = group.links.filter((link: SeoLink) => {
          const hasUndefinedInUrl = link.uriV2 && link.uriV2.includes('/undefined/');

          if (hasUndefinedInUrl) {
            logger?.warn('Filtered out SEO footer link with /undefined/ in URL', {
              pathname,
              linkName: link.name,
              linkUrl: link.uriV2,
              groupHeader: group.header,
            });
          }

          return !hasUndefinedInUrl;
        });

        return {
          ...group,
          links: validLinks,
        };
      })
      .filter((group: SeoLinkGroup) => group.links.length > 0); // Remove groups with no valid links

    return {
      ...seoLinks,
      groups: filteredGroups,
    };
  }, [seoLinks, pathname]);

  // Render mobile layout using SrpContentWrapper
  if (isMobile) {
    return (
      <SrpContentWrapper
        identifier="seo-footer"
        customHeader={
          shouldShowMarketingBlurb ? (
            <header>
              <SharedMarketingBlurb />
            </header>
          ) : null
        }
        background="backgroundGrey"
        fluid
        border="borderTop"
      >
        {shouldShowMarketingBlurb && !hideDivider ? <S.SemanticDivider shortDivider={shortDivider} /> : null}
        <ul>
          <MobileLinkGroup
            header={NEAR_BY_RENTALS_GROUP.header}
            links={NEAR_BY_RENTALS_GROUP.links}
            key="seo-footer-mobile-link-group-near-by-rentals"
          />
          {filteredSeoLinks.groups &&
            filteredSeoLinks.groups.map((group: SeoLinkGroup) => {
              const hasLinks = group.links.length > 0;

              if (!hasLinks) {
                return null;
              }

              return (
                hasLinks && (
                  <MobileLinkGroup
                    header={group.header}
                    links={group.links}
                    key={`seo-footer-mobile-link-group-${group.header}`}
                  />
                )
              );
            })}
        </ul>
      </SrpContentWrapper>
    );
  }

  // Render desktop layout using desktop styles
  return (
    <S.DesktopSeoFooterArticle className={className}>
      <S.DesktopSemanticDivider shortDivider={shortDivider} hideDivider={hideDivider} />
      {shouldShowMarketingBlurb && (
        <header>
          <SharedMarketingBlurb />
        </header>
      )}
      <S.DesktopSeoFooterSectionList>
        <DesktopLinkGroup
          header={NEAR_BY_RENTALS_GROUP.header}
          links={NEAR_BY_RENTALS_GROUP.links}
          key="seo-footer-desktop-link-group-near-by-rentals"
        />
        {filteredSeoLinks.groups &&
          filteredSeoLinks.groups.map((group: SeoLinkGroup) => {
            const hasLinks = group.links.length > 0;

            if (!hasLinks) {
              return null;
            }

            return (
              hasLinks && (
                <DesktopLinkGroup
                  header={group.header}
                  links={group.links}
                  key={`seo-footer-desktop-link-group-${group.header}`}
                />
              )
            );
          })}
      </S.DesktopSeoFooterSectionList>
    </S.DesktopSeoFooterArticle>
  );
};

export default SeoFooter;
